import { useQuery } from '@tanstack/react-query';
import { BookingService } from '@/services/booking.service';
import { FacilityType } from '@/types/booking.types';
import { defaultQueryOptions } from '@/config/react-query.config';

export const availableDatesKeys = {
  all: ['available-dates'] as const,
  forLocation: (
    locationId: string,
    facility: FacilityType,
    bookingDate: string,
    teamMemberId?: string,
    roomId?: string,
    rescheduleBookingId?: string
  ) =>
    [
      ...availableDatesKeys.all,
      locationId,
      facility,
      bookingDate,
      teamMemberId ?? 'NO_COACH',
      roomId ?? 'NO_BAY',
      /* A reschedule and a fresh booking get different answers for the same
         day, so they must not share a cache entry. */
      rescheduleBookingId ?? 'NO_RESCHEDULE',
    ] as const,
};

/**
 * Which upcoming dates (from bookingDate, daysAhead days out) have any slot
 * at all — used to disable/skip dead dates in the date picker.
 */
export const useAvailableDates = (
  locationId: string,
  facility: FacilityType,
  bookingDate: string,
  timezone?: string,
  daysAhead?: number,
  teamMemberId?: string,
  roomId?: string,
  rescheduleBookingId?: string
) => {
  const query = useQuery<Record<string, boolean>, Error>({
    queryKey: availableDatesKeys.forLocation(
      locationId,
      facility,
      bookingDate,
      teamMemberId,
      roomId,
      rescheduleBookingId
    ),
    queryFn: () =>
      BookingService.getAvailableDates(
        locationId,
        facility,
        bookingDate,
        timezone,
        daysAhead,
        teamMemberId,
        roomId,
        rescheduleBookingId
      ),
    enabled: Boolean(locationId && facility && bookingDate) && facility !== FacilityType.HITTING_BAY,
    ...defaultQueryOptions,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
};
